/**
 * Tab Use (UI Pattern) Redux Slice
 * Manages state for UI pattern CRUD operations
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import type { RootState } from '../../store';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Types
export interface TabUseItem {
    tab_use_id: number;
    pattern_name: string;
    natural_description: string;
    default_ui_component_type: string;
    default_manage_types: string;
    usage_count: number;
}

export interface TabUseStats {
    total_patterns: number;
    total_tabs_with_patterns: number;
    unassigned_tabs: number;
    unique_component_types: number;
}

interface TabUseState {
    items: TabUseItem[];
    stats: TabUseStats | null;
    loading: boolean;
    error: string | null;
}

// Initial state
const initialState: TabUseState = {
    items: [],
    stats: null,
    loading: false,
    error: null,
};

// Async thunks
export const fetchTabUsePatterns = createAsyncThunk(
    'tabUse/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/tab-use`, {
                Manage: 4, // Get all
            });
            return response.data.data;
        } catch (error: any) {
            console.warn('[TabUse] API not available:', error.message);
            return rejectWithValue(
                error.response?.data?.message || 'Tab-use endpoint not available',
            );
        }
    },
);

export const fetchTabUseStats = createAsyncThunk(
    'tabUse/fetchStats',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/tab-use`, {
                Manage: 7, // Get statistics
            });
            return response.data.data[0];
        } catch (error: any) {
            console.warn('[TabUse] Stats API not available:', error.message);
            return rejectWithValue(error.response?.data?.message || 'Tab-use stats not available');
        }
    },
);

export const createTabUsePattern = createAsyncThunk(
    'tabUse/create',
    async (data: Partial<TabUseItem>, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/tab-use`, {
                manage: 1, // Create
                pattern_name: data.pattern_name,
                natural_description: data.natural_description,
                default_ui_component_type: data.default_ui_component_type,
                default_manage_types: data.default_manage_types,
            });
            return response.data.data[0];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create pattern');
        }
    },
);

export const updateTabUsePattern = createAsyncThunk(
    'tabUse/update',
    async (data: Partial<TabUseItem>, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/tab-use`, {
                manage: 2, // Update
                tab_use_id: data.tab_use_id,
                pattern_name: data.pattern_name,
                natural_description: data.natural_description,
                default_ui_component_type: data.default_ui_component_type,
                default_manage_types: data.default_manage_types,
            });
            return response.data.data[0];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update pattern');
        }
    },
);

export const deleteTabUsePattern = createAsyncThunk(
    'tabUse/delete',
    async (tabUseId: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/tab-use`, {
                Manage: 3, // Delete
                tab_use_id: tabUseId,
            });

            // Check for error response
            if (response.data.data[0]?.Status === 'ERROR') {
                throw new Error(response.data.data[0].Message);
            }
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Failed to delete pattern',
            );
        }

        return tabUseId;
    },
);

// Slice
const tabUseSlice = createSlice({
    name: 'tabUse',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch all patterns
        builder.addCase(fetchTabUsePatterns.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTabUsePatterns.fulfilled, (state, action) => {
            state.loading = false;
            state.items = action.payload;
        });
        builder.addCase(fetchTabUsePatterns.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch patterns';
        });

        // Fetch stats
        builder.addCase(fetchTabUseStats.fulfilled, (state, action) => {
            state.stats = action.payload;
        });

        // Create pattern
        builder.addCase(createTabUsePattern.fulfilled, (state, action) => {
            state.items.push(action.payload);
        });

        // Update pattern
        builder.addCase(updateTabUsePattern.fulfilled, (state, action) => {
            const index = state.items.findIndex(
                (item) => item.tab_use_id === action.payload.tab_use_id,
            );
            if (index !== -1) {
                state.items[index] = action.payload;
            }
        });

        // Delete pattern
        builder.addCase(deleteTabUsePattern.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.tab_use_id !== action.payload);
        });
        builder.addCase(deleteTabUsePattern.rejected, (state, action) => {
            state.error = action.error.message || 'Failed to delete pattern';
        });
    },
});

// Actions
export const { clearError } = tabUseSlice.actions;

// Selectors
export const selectTabUseItems = (state: RootState) => state.tabUse.items;
export const selectTabUseStats = (state: RootState) => state.tabUse.stats;
export const selectTabUseLoading = (state: RootState) => state.tabUse.loading;
export const selectTabUseError = (state: RootState) => state.tabUse.error;

export default tabUseSlice.reducer;
